<div class="scheduled-order-form">
  <!-- LEFT SIDEBAR: Compact Stepper Navigation -->
  <div class="left-sidebar" [class.collapsed]="sidebarCollapsed()">
    <!-- Collapse Toggle Button -->
    <!-- Item 17: Added hint text next to collapse button -->
    <div class="sidebar-header">
      <button
        type="button"
        class="collapse-toggle"
        (click)="toggleSidebar()"
        [attr.aria-label]="
          sidebarCollapsed() ? 'Expand sidebar' : 'Collapse sidebar'
        "
      >
        <i
          [class]="
            sidebarCollapsed() ? 'pi pi-angle-right' : 'pi pi-angle-left'
          "
        ></i>
      </button>
      @if (!sidebarCollapsed()) {
        <small class="collapse-hint">Collapse</small>
      }
    </div>

    <!-- Mobile Linear Progress Indicator (Collapsible) with Navigation -->
    <div class="mobile-linear-progress" *ngIf="isMobile()">
      <div class="linear-progress-wrapper">
        <!-- Previous Button -->
        <button
          type="button"
          class="nav-button nav-button-prev"
          (click)="goToPreviousStep()"
          [disabled]="currentStep() === 0"
          [attr.aria-label]="'Go to previous step'"
        >
          <i class="pi pi-chevron-left"></i>
        </button>

        <!-- Progress Toggle (center) -->
        <button
          type="button"
          class="linear-progress-toggle"
          (click)="toggleMobileStepper()"
          [attr.aria-label]="
            mobileStepperExpanded() ? 'Collapse stepper' : 'Expand stepper'
          "
          [attr.aria-expanded]="mobileStepperExpanded()"
        >
          <!-- Progress Bar -->
          <div class="progress-bar-container">
            <div
              class="progress-bar-fill"
              [style.width.%]="getProgressPercentage()"
            ></div>
          </div>

          <!-- Current Step Label -->
          <div class="progress-label">
            <span class="step-name">{{ getCurrentStepName() }}</span>
            <span class="step-count"
              >{{ currentStep() + 1 }} of {{ getTotalSteps() }}</span
            >
          </div>

          <!-- Expand/Collapse Icon -->
          <i
            class="pi expand-collapse-icon"
            [class.pi-chevron-down]="!mobileStepperExpanded()"
            [class.pi-chevron-up]="mobileStepperExpanded()"
          ></i>
        </button>

        <!-- Next Button -->
        <button
          type="button"
          class="nav-button nav-button-next"
          (click)="goToNextStep()"
          [disabled]="!canGoToNextStep()"
          [attr.aria-label]="'Go to next step'"
        >
          <i class="pi pi-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Collapsible Step Accordion -->
    <div
      class="step-accordion"
      [class.mobile-expanded]="isMobile() && mobileStepperExpanded()"
      [class.mobile-collapsed]="isMobile() && !mobileStepperExpanded()"
    >
      <!-- Step 0: Summary (Edit mode only) -->
      <div
        *ngIf="showValidation"
        class="accordion-item"
        [class.active]="currentStep() === 0"
        [class.completed]="currentStep() > 0"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(0)"
          pTooltip="Summary"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span class="step-number" [class.has-check]="currentStep() > 0">
              <span *ngIf="currentStep() > 0" class="check-icon">✓</span>
              <span *ngIf="currentStep() <= 0">0</span>
            </span>
            <div class="step-info">
              <span class="step-label">Summary</span>
              <span *ngIf="currentStep() > 0" class="completion-badge"
                >Completed</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === 0"
          ></i>
        </button>
      </div>

      <!-- Step 1 (or 0): Customer -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 1 : 0)"
        [class.completed]="
          currentStep() > (showValidation ? 1 : 0) &&
          isStepComplete(showValidation ? 1 : 0)
        "
        [class.error]="isStepInvalid(showValidation ? 1 : 0)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 1 : 0)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 1 : 0)"
          pTooltip="Customer"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 1 : 0) &&
                isStepComplete(showValidation ? 1 : 0)
              "
              [class.has-error]="isStepInvalid(showValidation ? 1 : 0)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 1 : 0) &&
                  isStepComplete(showValidation ? 1 : 0)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 1 : 0)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 1 : 0) &&
                  !isStepInvalid(showValidation ? 1 : 0)
                "
                >{{ showValidation ? 1 : 1 }}</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Customer</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 1 : 0) &&
                  isStepComplete(showValidation ? 1 : 0)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 1 : 0)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 1 : 0)"
          ></i>
        </button>
      </div>

      <!-- Step 2 (or 1): Products -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 2 : 1)"
        [class.completed]="
          currentStep() > (showValidation ? 2 : 1) &&
          isStepComplete(showValidation ? 2 : 1)
        "
        [class.error]="isStepInvalid(showValidation ? 2 : 1)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 2 : 1)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 2 : 1)"
          pTooltip="Products"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 2 : 1) &&
                isStepComplete(showValidation ? 2 : 1)
              "
              [class.has-error]="isStepInvalid(showValidation ? 2 : 1)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 2 : 1) &&
                  isStepComplete(showValidation ? 2 : 1)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 2 : 1)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 2 : 1) &&
                  !isStepInvalid(showValidation ? 2 : 1)
                "
                >2</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Products</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 2 : 1) &&
                  isStepComplete(showValidation ? 2 : 1)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 2 : 1)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 2 : 1)"
          ></i>
        </button>
      </div>

      <!-- Step 3 (or 2): Schedule -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 3 : 2)"
        [class.completed]="
          currentStep() > (showValidation ? 3 : 2) &&
          isStepComplete(showValidation ? 3 : 2)
        "
        [class.error]="isStepInvalid(showValidation ? 3 : 2)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 3 : 2)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 3 : 2)"
          pTooltip="Schedule"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 3 : 2) &&
                isStepComplete(showValidation ? 3 : 2)
              "
              [class.has-error]="isStepInvalid(showValidation ? 3 : 2)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 3 : 2) &&
                  isStepComplete(showValidation ? 3 : 2)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 3 : 2)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 3 : 2) &&
                  !isStepInvalid(showValidation ? 3 : 2)
                "
                >3</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Schedule</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 3 : 2) &&
                  isStepComplete(showValidation ? 3 : 2)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 3 : 2)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 3 : 2)"
          ></i>
        </button>
      </div>

      <!-- Step 4 (or 3): Payment -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 4 : 3)"
        [class.completed]="
          currentStep() > (showValidation ? 4 : 3) &&
          isStepComplete(showValidation ? 4 : 3)
        "
        [class.error]="isStepInvalid(showValidation ? 4 : 3)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 4 : 3)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 4 : 3)"
          pTooltip="Payment"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 4 : 3) &&
                isStepComplete(showValidation ? 4 : 3)
              "
              [class.has-error]="isStepInvalid(showValidation ? 4 : 3)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 4 : 3) &&
                  isStepComplete(showValidation ? 4 : 3)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 4 : 3)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 4 : 3) &&
                  !isStepInvalid(showValidation ? 4 : 3)
                "
                >4</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Payment</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 4 : 3) &&
                  isStepComplete(showValidation ? 4 : 3)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 4 : 3)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 4 : 3)"
          ></i>
        </button>
      </div>

      <!-- Step 5 (or 4): Shipping Address -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 5 : 4)"
        [class.completed]="
          currentStep() > (showValidation ? 5 : 4) &&
          isStepComplete(showValidation ? 5 : 4)
        "
        [class.error]="isStepInvalid(showValidation ? 5 : 4)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 5 : 4)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 5 : 4)"
          pTooltip="Shipping Address"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 5 : 4) &&
                isStepComplete(showValidation ? 5 : 4)
              "
              [class.has-error]="isStepInvalid(showValidation ? 5 : 4)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 5 : 4) &&
                  isStepComplete(showValidation ? 5 : 4)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 5 : 4)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 5 : 4) &&
                  !isStepInvalid(showValidation ? 5 : 4)
                "
                >5</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Shipping</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 5 : 4) &&
                  isStepComplete(showValidation ? 5 : 4)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 5 : 4)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 5 : 4)"
          ></i>
        </button>
      </div>

      <!-- Step 6 (or 5): Coupons -->
      <!-- Item 16: Renamed from "Coupons and Notes" - Notes moved to Additional Options -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 6 : 5)"
        [class.completed]="
          currentStep() > (showValidation ? 6 : 5) &&
          isStepComplete(showValidation ? 6 : 5)
        "
        [class.error]="isStepInvalid(showValidation ? 6 : 5)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 6 : 5)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 6 : 5)"
          pTooltip="Coupons"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 6 : 5) &&
                isStepComplete(showValidation ? 6 : 5)
              "
              [class.has-error]="isStepInvalid(showValidation ? 6 : 5)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 6 : 5) &&
                  isStepComplete(showValidation ? 6 : 5)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 6 : 5)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 6 : 5) &&
                  !isStepInvalid(showValidation ? 6 : 5)
                "
                >6</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Coupons</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 6 : 5) &&
                  isStepComplete(showValidation ? 6 : 5)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 6 : 5)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 6 : 5)"
          ></i>
        </button>
      </div>

      <!-- Step 7 (or 6): Additional Options -->
      <!-- Item 16: New separate step for Custom Name and Notes -->
      <div
        class="accordion-item"
        [class.active]="currentStep() === (showValidation ? 7 : 6)"
        [class.completed]="
          currentStep() > (showValidation ? 7 : 6) &&
          isStepComplete(showValidation ? 7 : 6)
        "
        [class.error]="isStepInvalid(showValidation ? 7 : 6)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(showValidation ? 7 : 6)"
          [disabled]="disabled || !canNavigateToStep(showValidation ? 7 : 6)"
          pTooltip="Additional Options"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="
                currentStep() > (showValidation ? 7 : 6) &&
                isStepComplete(showValidation ? 7 : 6)
              "
              [class.has-error]="isStepInvalid(showValidation ? 7 : 6)"
            >
              <span
                *ngIf="
                  currentStep() > (showValidation ? 7 : 6) &&
                  isStepComplete(showValidation ? 7 : 6)
                "
                class="check-icon"
                >✓</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 7 : 6)"
                class="error-icon"
                >!</span
              >
              <span
                *ngIf="
                  currentStep() <= (showValidation ? 7 : 6) &&
                  !isStepInvalid(showValidation ? 7 : 6)
                "
                >7</span
              >
            </span>
            <div class="step-info">
              <span class="step-label">Options</span>
              <span
                *ngIf="
                  currentStep() > (showValidation ? 7 : 6) &&
                  isStepComplete(showValidation ? 7 : 6)
                "
                class="completion-badge"
                >Completed</span
              >
              <span
                *ngIf="isStepInvalid(showValidation ? 7 : 6)"
                class="error-badge"
                >Incomplete</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === (showValidation ? 7 : 6)"
          ></i>
        </button>
      </div>

      <!-- Step 8: Validation Checks (Edit mode only) -->
      <!-- Item 16: Updated from Step 7 to Step 8 -->
      <div
        *ngIf="showValidation"
        class="accordion-item"
        [class.active]="currentStep() === 8"
        [class.completed]="isStepComplete(8)"
        [class.error]="!isStepComplete(8)"
      >
        <button
          type="button"
          class="accordion-header"
          (click)="currentStep.set(8)"
          pTooltip="Validation Checks"
          tooltipPosition="right"
        >
          <div class="step-indicator-wrapper">
            <span
              class="step-number"
              [class.has-check]="isStepComplete(8)"
              [class.has-error]="!isStepComplete(8)"
            >
              <span *ngIf="isStepComplete(8)" class="check-icon">✓</span>
              <span *ngIf="!isStepComplete(8)" class="error-icon">!</span>
            </span>
            <div class="step-info">
              <span class="step-label">Validation</span>
              <span *ngIf="isStepComplete(8)" class="completion-badge"
                >Passed</span
              >
              <span *ngIf="!isStepComplete(8)" class="error-badge"
                >Has Issues</span
              >
            </div>
          </div>
          <i
            class="pi pi-chevron-down expand-icon"
            [class.rotated]="currentStep() === 8"
          ></i>
        </button>
      </div>
    </div>

    <!-- Action Buttons at Bottom of Sidebar -->
    <!-- Item 17: Renamed back button to "Back to List" -->
    <div class="sidebar-actions" *ngIf="showActions">
      <div class="action-buttons">
        <p-button
          label="Back to List"
          icon="pi pi-arrow-left"
          [outlined]="true"
          severity="secondary"
          size="small"
          (onClick)="formCancel.emit()"
          [disabled]="isSaving"
          styleClass="w-full"
        />
        <p-button
          [label]="mode === 'create' ? 'Create Order' : 'Save Changes'"
          icon="pi pi-save"
          size="small"
          (onClick)="formSubmit.emit()"
          [disabled]="!canSubmit || isSaving"
          [loading]="isSaving"
          styleClass="w-full"
        />
      </div>
    </div>
  </div>

  <!-- CENTER: Main Content Area -->
  <div class="center-content">
    <!-- Step 0: Summary (Edit mode only) -->
    <div *ngIf="showValidation && currentStep() === 0" class="step-content">
      <app-section-banner
        title="Order Summary"
        subtitle="Review the current state and details of this scheduled order"
      />

      <div class="content-wrapper">
        <!-- Order summary component with navigation events -->
        <app-scheduled-order-summary
          [scheduledOrder]="scheduledOrder"
          [totals]="totals"
          (navigateToCustomer)="onNavigateToCustomer()"
          (navigateToProducts)="onNavigateToProducts()"
          (navigateToSchedule)="onNavigateToSchedule()"
          (navigateToCoupons)="onNavigateToCoupons()"
          (navigateToValidation)="onNavigateToValidation()"
          (openTotalsPopover)="onOpenTotalsPopover($event)"
        />
      </div>
    </div>

    <!-- Step 1 (or 0): Customer Selection -->
    <div
      *ngIf="currentStep() === (showValidation ? 1 : 0)"
      class="step-content"
    >
      <app-section-banner
        title="Customer Selection"
        subtitle="Select a customer to create the scheduled order"
      />

      <div class="content-wrapper">
        <div class="form-section">
          <app-customer-selector
            [selectedCustomer]="formGroup.controls.customer.value"
            [siteId]="siteId"
            [disabled]="disabled"
            [required]="true"
            [isEditMode]="mode === 'edit'"
            [accessToken]="accessToken"
            (customerChange)="onCustomerChange($event)"
          />
        </div>
      </div>
    </div>

    <!-- Step 2 (or 1): Order Items -->
    <div
      *ngIf="currentStep() === (showValidation ? 2 : 1)"
      class="step-content"
    >
      <!-- Section Banner -->
      <app-section-banner
        title="Products"
        subtitle="Add products to the order"
      />

      <!-- Content with padding -->
      <div class="content-wrapper">
        <!-- Accordion for Items Only -->
        <p-accordion
          [multiple]="true"
          [(value)]="productsAccordionActiveValue"
          styleClass="compact-accordion"
        >
          <!-- Order Items Section -->
          <p-accordionpanel value="0">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-shopping-cart"></i>
                <span>Order Items</span>
                <span class="required-badge">Required</span>
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-section-content">
                <app-item-manager
                  [itemsFormArray]="formGroup.controls.scheduledOrderItems"
                  [siteId]="siteId"
                  [disabled]="disabled"
                  [mode]="mode"
                  [required]="true"
                  [accessToken]="accessToken"
                />
              </div>
            </p-accordioncontent>
          </p-accordionpanel>
        </p-accordion>
      </div>
    </div>

    <!-- Step 3 (or 2): Schedule Configuration -->
    <div
      *ngIf="currentStep() === (showValidation ? 3 : 2)"
      class="step-content"
    >
      <!-- Section Banner -->
      <app-section-banner
        title="Schedule Configuration"
        subtitle="Configure when and how often this order should process"
      />

      <!-- Content with padding -->
      <div class="content-wrapper">
        <div class="form-section">
          <app-frequency-editor
            [formGroup]="formGroup"
            [site]="site"
            [disabled]="disabled"
            [advancedOptionsAllowed]="advancedOptionsAllowed"
            [tierName]="tierName"
            [upgradeUrl]="upgradeUrl"
            [accessToken]="accessToken"
          />
        </div>
      </div>
    </div>

    <!-- Step 4 (or 3): Payment Method -->
    <div
      *ngIf="currentStep() === (showValidation ? 4 : 3)"
      class="step-content"
    >
      <app-section-banner
        title="Payment Method"
        subtitle="Select how this order will be charged"
      />

      <div class="content-wrapper">
        <div class="form-section">
          <app-payment-selector
            [formGroup]="formGroup"
            [siteId]="siteId"
            [disabled]="disabled"
            [required]="true"
            [accessToken]="accessToken"
            (newPaymentMethodValidChange)="
              onNewPaymentMethodValidChange($event)
            "
          />
        </div>
      </div>
    </div>

    <!-- Step 5 (or 4): Shipping Address -->
    <div
      *ngIf="currentStep() === (showValidation ? 5 : 4)"
      class="step-content"
    >
      <app-section-banner
        title="Shipping Address"
        subtitle="Configure where this order will be shipped"
      />

      <div class="content-wrapper">
        <div class="form-section">
          <app-shipping-selector
            [formGroup]="formGroup"
            [siteId]="siteId"
            [disabled]="disabled"
            [required]="false"
            [accessToken]="accessToken"
            (addressChanged)="onAddressChanged()"
          />
        </div>

        <!-- Traditional QPilot Shipping Rates (when Nextime is disabled OR when shouldDisplayDeliveryDate is false) -->
        <!-- IMPORTANT: Only show in EDIT mode, not in CREATE mode (matches old QMC behavior) -->
        <div
          *ngIf="
            mode === 'edit' &&
            (!hasNextime || (hasNextime && !nextimeShouldDisplayDeliveryDate))
          "
          class="form-section"
        >
          <p-accordion [activeIndex]="0" styleClass="compact-accordion">
            <p-accordionpanel value="0">
              <p-accordionheader>
                <div class="accordion-header-content">
                  <i class="pi pi-box"></i>
                  <span>{{ getShippingRatesTitleText() }}</span>
                  <i
                    *ngIf="hasNextime && !nextimeShouldDisplayDeliveryDate"
                    class="pi pi-info-circle info-icon-small"
                    pTooltip="Nextime integration is active but delivery date selection is disabled"
                    tooltipPosition="top"
                  ></i>
                </div>
              </p-accordionheader>
              <p-accordioncontent>
                <div class="accordion-section-content">
                  <app-shipping-rate-selector
                    [formGroup]="formGroup"
                    [orderData]="getScheduledOrderForShippingRates()"
                    [shippingRateOptions]="shippingRateOptions"
                    [siteId]="siteId"
                    [disabled]="disabled"
                    [required]="false"
                    [accessToken]="accessToken"
                    (shippingRateOptionsLoaded)="
                      onShippingRateOptionsLoaded($event)
                    "
                    (autoRateToggled)="onAutoRateToggled($event)"
                  />
                </div>
              </p-accordioncontent>
            </p-accordionpanel>
          </p-accordion>
        </div>

        <!-- Nextime Delivery Date Selection UI (modernized for new QMC) -->
        <!-- Show if Nextime exists and delivery date display is enabled -->
        <!-- IMPORTANT: Only show in EDIT mode, not in CREATE mode (matches old QMC behavior) -->
        <div
          *ngIf="
            mode === 'edit' && hasNextime && nextimeShouldDisplayDeliveryDate
          "
          class="form-section nextime-section"
        >
          <p-accordion
            [activeIndex]="0"
            styleClass="nextime-accordion compact-accordion"
          >
            <p-accordionpanel value="0">
              <p-accordionheader>
                <div class="accordion-header-content">
                  <i class="pi pi-calendar-clock"></i>
                  <span>Nextime Delivery Options</span>
                  <i
                    class="pi pi-info-circle info-icon-small"
                    pTooltip="Select a delivery date and shipping method from Nextime integration"
                    tooltipPosition="top"
                  ></i>
                </div>
              </p-accordionheader>
              <p-accordioncontent>
                <div class="nextime-content-wrapper">
                  <!-- Delivery Date Selector -->
                  <div class="nextime-field-group">
                    <label
                      for="nextime-delivery-date"
                      class="nextime-field-label"
                    >
                      Select Delivery Date
                      <span class="required-star">*</span>
                    </label>
                    <p-select
                      inputId="nextime-delivery-date"
                      [(ngModel)]="selectedDeliveryDate"
                      [options]="nextimeShippingOptions?.deliveryDates || []"
                      (onChange)="updateNextimeShippingRate()"
                      placeholder="Choose a delivery date"
                      optionLabel="deliveryDate"
                      [disabled]="disabled"
                      styleClass="w-full modern-select"
                      [showClear]="true"
                      appendTo="body"
                    >
                      <ng-template let-date pTemplate="item">
                        <div class="delivery-date-dropdown-item">
                          <div class="date-main">
                            <i class="pi pi-calendar"></i>
                            <span class="delivery-date-text">
                              {{ date.deliveryDate | date: "EEEE, MMMM d, y" }}
                            </span>
                          </div>
                          <div class="date-cutoff">
                            <i class="pi pi-clock"></i>
                            <span>
                              Order by
                              {{
                                date.shippingCutOffDate | date: "MMM d, h:mm a"
                              }}
                            </span>
                          </div>
                        </div>
                      </ng-template>
                      <ng-template let-date pTemplate="selectedItem">
                        <div class="delivery-date-selected" *ngIf="date">
                          <i class="pi pi-calendar"></i>
                          <span>
                            {{ date.deliveryDate | date: "mediumDate" }}
                          </span>
                        </div>
                      </ng-template>
                    </p-select>
                  </div>

                  <!-- Shipping Method Radio Buttons -->
                  <div
                    *ngIf="
                      selectedDeliveryDate &&
                      selectedDeliveryDate.shippingLines &&
                      selectedDeliveryDate.shippingLines.length > 0
                    "
                    class="nextime-field-group shipping-methods-group"
                  >
                    <label class="nextime-field-label">
                      Select Shipping Method
                      <span class="required-star">*</span>
                    </label>
                    <div class="shipping-methods-grid">
                      <div
                        *ngFor="
                          let shippingLine of selectedDeliveryDate.shippingLines;
                          let i = index
                        "
                        class="shipping-method-card"
                        [class.selected]="selectedShippingLine === shippingLine"
                        (click)="
                          !disabled && (selectedShippingLine = shippingLine);
                          updateNextimeShippingRate()
                        "
                      >
                        <div class="shipping-card-radio">
                          <p-radiobutton
                            [name]="'shippingLine'"
                            [value]="shippingLine"
                            [(ngModel)]="selectedShippingLine"
                            (onClick)="updateNextimeShippingRate()"
                            [disabled]="disabled"
                            [inputId]="'shipping-' + i"
                          />
                        </div>
                        <div class="shipping-card-content">
                          <div class="shipping-card-header">
                            <label
                              [for]="'shipping-' + i"
                              class="shipping-name"
                            >
                              {{ shippingLine.name }}
                            </label>
                            <span class="shipping-price">
                              ${{ shippingLine.total | number: "1.2-2" }}
                            </span>
                          </div>
                          <div class="shipping-card-method">
                            <i class="pi pi-box"></i>
                            <span>{{ shippingLine.shippingMethod }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- No Shipping Lines Message -->
                  <div
                    *ngIf="
                      selectedDeliveryDate &&
                      (!selectedDeliveryDate.shippingLines ||
                        selectedDeliveryDate.shippingLines.length === 0)
                    "
                    class="nextime-empty-state"
                  >
                    <i class="pi pi-exclamation-triangle"></i>
                    <div class="empty-state-content">
                      <h4>No Shipping Methods Available</h4>
                      <p>
                        There are no shipping methods available for the selected
                        delivery date. Please choose a different date.
                      </p>
                    </div>
                  </div>
                </div>
              </p-accordioncontent>
            </p-accordionpanel>
          </p-accordion>
        </div>
      </div>
    </div>

    <!-- Step 6 (or 5): Coupons -->
    <!-- Item 16: Separated Coupons from Additional Options -->
    <div
      *ngIf="currentStep() === (showValidation ? 6 : 5)"
      class="step-content"
    >
      <app-section-banner
        title="Coupons"
        subtitle="Apply discount codes to the order"
      />

      <div class="content-wrapper">
        <!-- Accordion for Coupon Sections -->
        <p-accordion
          [multiple]="true"
          [(value)]="couponsAccordionActiveValue"
          styleClass="compact-accordion"
        >
          <!-- Coupon Codes Section -->
          <p-accordionpanel value="0">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-ticket"></i>
                <span>Coupon Codes</span>
                <span class="optional-badge">Optional</span>
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-section-content">
                <app-coupon-manager
                  [formGroup]="formGroup"
                  [siteId]="siteId"
                  [mode]="mode"
                  [scheduledOrder]="scheduledOrder"
                  [disabled]="disabled"
                  [accessToken]="accessToken"
                />
              </div>
            </p-accordioncontent>
          </p-accordionpanel>
        </p-accordion>
      </div>
    </div>

    <!-- Step 7 (or 6): Additional Options -->
    <!-- Item 16: Wrapped in accordion like other steps -->
    <div
      *ngIf="currentStep() === (showValidation ? 7 : 6)"
      class="step-content"
    >
      <app-section-banner
        title="Additional Options"
        subtitle="Add custom name and notes to the order"
      />

      <div class="content-wrapper">
        <!-- Item 16: Accordion wrapper matching other steps -->
        <p-accordion
          [multiple]="true"
          [(value)]="optionsAccordionActiveValue"
          styleClass="compact-accordion"
        >
          <!-- Additional Options Section -->
          <p-accordionpanel value="0">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-cog"></i>
                <span>Custom Name & Notes</span>
                <span class="optional-badge">Optional</span>
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-section-content">
                <div class="form-section">
                  <!-- Customer's Next Occurrence Date/Time (Read-only info) -->
                  <div
                    class="form-field"
                    *ngIf="getCustomerNextOccurrence() && nextOccurrenceTime"
                  >
                    <label>Customer's Next Occurrence Date/Time</label>
                    <div class="review-field-value">
                      {{
                        getCustomerNextOccurrence()
                          | date: "MMMM d, y 'at' hh:mm a"
                      }}
                      {{
                        getFormattedUtcOffset(
                          formGroup.controls.utcOffset.value || null
                        )
                      }}
                    </div>
                  </div>

                  <!-- Scheduled Order will Process on (Read-only info) -->
                  <div class="form-field" *ngIf="getProcessingTime()">
                    <label>Scheduled Order will Process on</label>
                    <div class="review-field-value">
                      {{ getProcessingTime() | date: "MMMM d, y 'at' hh:mm a" }}
                      {{
                        getFormattedUtcOffset(
                          formGroup.controls.utcOffset.value ||
                            site?.UtcOffset ||
                            0
                        )
                      }}
                    </div>
                  </div>

                  <div class="form-field">
                    <label for="custom-name">Custom Name</label>
                    <p class="field-description">
                      Name this Scheduled Order for internal use only
                    </p>
                    <input
                      pInputText
                      id="custom-name"
                      [formControl]="formGroup.controls.name"
                      [disabled]="disabled"
                      placeholder="Enter a custom name"
                      class="w-full"
                    />
                  </div>

                  <div class="form-field">
                    <label for="note">Note</label>
                    <p class="field-description">
                      Add, view, and edit notes on the Scheduled Order. May be
                      visible to your customer depending on
                      <a
                        href="https://docs.qpilot.cloud/docs/general-settings"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="settings-link"
                        >your site settings</a
                      >.
                    </p>
                    <textarea
                      pTextarea
                      id="note"
                      [formControl]="formGroup.controls.note"
                      [disabled]="disabled"
                      placeholder="Add notes"
                      class="w-full"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              </div>
            </p-accordioncontent>
          </p-accordionpanel>
        </p-accordion>
      </div>
    </div>

    <!-- Step 8: Validation Checks (Edit mode only) -->
    <!-- Item 16: Updated from Step 7 to Step 8 -->
    <div *ngIf="showValidation && currentStep() === 8" class="step-content">
      <app-section-banner
        title="Validation Checks"
        subtitle="Review validation status and processing history for this scheduled order"
      />

      <div class="content-wrapper">
        <!-- Accordion for Validation & Processing Section -->
        <p-accordion
          [multiple]="false"
          [value]="'0'"
          styleClass="compact-accordion"
        >
          <!-- Validation & Processing Section -->
          <p-accordionpanel value="0">
            <p-accordionheader>
              <div class="accordion-header-content">
                <i class="pi pi-shield"></i>
                <span>Validation & Processing</span>
                <!-- Status badge in header -->
                <p-tag
                  *ngIf="validationResult && validationResult.Succeeded"
                  value="All Passed"
                  severity="success"
                  [rounded]="true"
                  styleClass="mx-2"
                />
                <p-tag
                  *ngIf="validationResult && !validationResult.Succeeded"
                  value="Failed"
                  severity="danger"
                  [rounded]="true"
                  styleClass="mx-2"
                />
              </div>
            </p-accordionheader>
            <p-accordioncontent>
              <div class="accordion-section-content">
                <!-- Validation checks inline view (includes both processing cycle and validation checks) -->
                <app-validation-checks-inline-view
                  *ngIf="scheduledOrder"
                  [scheduledOrder]="scheduledOrder"
                  [site]="site"
                  [inlineMode]="true"
                  [showLastProcessingCycle]="mode === 'edit'"
                  (viewCycleDetails)="viewCycleDetails.emit($event)"
                  (viewProcessingLogs)="viewProcessingLogs.emit($event)"
                />
              </div>
            </p-accordioncontent>
          </p-accordionpanel>
        </p-accordion>
      </div>
    </div>
  </div>

  <!-- Subscription Upgrade Dialog -->
  <app-subscription-upgrade-dialog
    [(visible)]="showUpgradeDialog"
    [featureName]="
      site && site.SupportsProcessingWindow
        ? 'Advanced Options for Date/Time and Processing'
        : 'Advanced Options for Processing'
    "
    [tierName]="tierName"
    [upgradeUrl]="upgradeUrl"
  />

  <!-- RIGHT SIDEBAR: Sticky Order Summary (hidden on mobile) -->
  <div class="right-sidebar" *ngIf="!isMobile()">
    <div class="order-summary-sticky">
      <h3 class="summary-title">Order Total Summary</h3>
      <app-totals-display
        [totals]="totals"
        [isLoading]="isCalculatingTotals"
        [showTitle]="false"
        [hasDisabledItems]="hasDisabledItems()"
      />

      <!-- Show validation errors if any -->
      <div *ngIf="getValidationErrors().length > 0" class="validation-errors">
        <p class="error-label">Please complete:</p>
        <ul class="error-list">
          <li *ngFor="let error of getValidationErrors()">{{ error }}</li>
        </ul>
      </div>
    </div>
  </div>
</div>
